<template>
  <label
    v-if="shouldDisplayInput"
    class="cursor-pointer hover-fade flow-page-user-input"
    :class="wrapperClasses"
    :data-type="type"
    :data-name="name"
    :data-required="required"
    :data-label="label"
    :data-input-value-key="inputValueKey"
  >
    <input
      v-if="type === 'checkbox'"
      type="checkbox"
      :checked="value"
      :key="inputValueKey + '_checkbox'"
      :required="required"
      @change="onInput"
      :disabled="isInputsLocked"
    />
    <slot>{{ label }}</slot>
    <!-- 閲覧モードかつ署名値がある場合: 軽量表示（Data URL / u/ パス） -->
    <span class="mb-2" v-if="(type === 'signature' || label === 'ご署名') && isInputsLocked && isSignatureValue">
      <SignatureImageDisplay :src="value" />
    </span>
    <!-- 編集モード（医院側など）: 従来の署名パッド -->
    <span class="mb-2" v-if="(type === 'signature' || label === 'ご署名') && !isInputsLocked && (isDisplayFromClincUser || isSignatureValue)">
      <SignaturePadInputWithButton
        v-model="value"
        :disabled="isInputsLocked"
      />
    </span>
    <!-- type が text のときは TS 上 signature と両立しないため、ご署名ラベル + 署名値のときだけテキスト欄を隠す -->
    <span v-if="type === 'text' && !(label === 'ご署名' && isSignatureValue)">
      <input
        type="text"
        :key="inputValueKey + '_text'"
        :required="required"
        :value="value"
        @input="onInput"
        :disabled="isInputsLocked"
      />
    </span>
    <span v-if="type === 'select'">
      <div class="d-flex flex-wrap gap-2 align-items-center">
        <label
          v-for="(option, index) in parsedOptions"
          :key="inputValueKey + '_option_' + index"
          class="d-flex align-items-center gap-1"
        >
          <input
            type="radio"
            :value="option"
            v-model="value"
            :disabled="isInputsLocked"
          />
          {{ option }}
        </label>
      </div>
    </span>
    <span v-if="type === 'multiselect'">
      <div class="d-flex flex-wrap gap-2 align-items-center">
        <label
          v-for="(option, index) in parsedOptions"
          :key="inputValueKey + '_option_' + index"
          class="d-flex align-items-center gap-1"
        >
          <input
            type="checkbox"
            :value="option"
            :checked="selectedValues.includes(option)"
            @change="onMultiselectChange(option, $event)"
            :disabled="isInputsLocked"
          />
          {{ option }}
        </label>
      </div>
    </span>
    <div
      v-if="errorMessage"
      class="flow-page-user-input--error-message text-danger small"
    >
      {{ errorMessage }}
    </div>
  </label>
</template>
<script lang="ts">
import { PropType, inject } from 'vue'
import SignatureImageDisplay from '../services/SignaturePadService/SignatureImageDisplay.vue'
import { AgreementFlowService } from './AgreementFlowService'

/**
 * FlowPageUserInput.vue
 *
 * Render like:
 * ```vue
 * <user-input
 *   type="checkbox"
 *   name="承認"
 *   required="true"
 * >
 * 当院より、動物の退院や引き取りの連絡があった場合には、速やかに引き取る旨、説明を受け、了解しました。
 * </user-input>
 * ```
 *
 * or
 *
 * ```vue
 * <user-input
 *   type="text"
 *   name="XXXX"
 *   required="true"
 * >
 * ご家族のご署名
 * </user-input>
 * ```
 *
 * or
 *
 * ```vue
 * <user-input
 *   type="multiselect"
 *   name="興味のある分野"
 *   options="技術,デザイン,マーケティング,営業"
 *   required="true"
 * >
 * 興味のある分野を選択してください（複数選択可）
 * </user-input>
 * ```
 */
export default {
  name: 'FlowPageUserInput',
  components: {
    SignatureImageDisplay,
  },
  emits: ['input'],
  inject: ['AgreementFlowService'],
  props: {
    type: {
      type: String as PropType<
        'text' | 'checkbox' | 'select' | 'multiselect' | 'signature'
      >,
      required: true,
    },
    name: {
      type: String,
      required: true,
    },
    required: {
      type: Boolean,
      default: false,
    },
    validation: {
      // RegExp
      type: String,
      default: '',
    },
    selections: {
      type: [Array, String] as PropType<string[] | string>,
      default: () => [],
    },
    label: {
      type: String,
      default: '',
    },
    options: {
      type: [String, Array] as PropType<string | string[]>,
      default: '',
    },
    // {勤務されている方:'その他'} のような形式で渡される Object
    enableIf: {
      type: Object as PropType<{
        [key: string]: any
      }>,
      default: () => ({}),
    },
  },
  data() {
    return {
      value: null as any,
    }
  },
  mounted() {
    this.value = this.$afs.userInputtedValues[this.inputValueKey] || null
  },
  computed: {
    inputValueKey(): string {
      // name or label
      return this.name || this.label
    },
    errorMessage(): string {
      if (!this.required) {
        return ''
      }
      // checkbox の場合は value が boolean であるため、null ではなく false になる
      if (
        this.value === null ||
        this.value === '' ||
        (this.type === 'checkbox' && !this.value) ||
        (this.type === 'multiselect' && this.selectedValues.length === 0)
      ) {
        return 'この項目は必須です'
      }
      return ''
    },
    $afs(): AgreementFlowService {
      return this.AgreementFlowService as AgreementFlowService
    },
    isInputsLocked(): boolean {
      // AgreementFlowServiceのlockUserInputsプロパティを参照し、入力をロックするか判定
      return this.$afs.lockUserInputs
    },
    parsedOptions(): string[] {
      if (Array.isArray(this.options)) {
        return this.options
      } else if (typeof this.options === 'string') {
        return this.options.split(',').map(option => option.trim())
      }
      return []
    },
    selectedValues(): string[] {
      if (this.type !== 'multiselect') {
        return []
      }
      if (!this.value || this.value === '') {
        return []
      }
      return this.value
        .split(',')
        .map((val: string) => val.trim())
        .filter((val: string) => val !== '')
    },
    shouldDisplayInput(): boolean {
      // enableIfが未設定または空の場合は表示
      if (!this.enableIf || Object.keys(this.enableIf).length === 0) {
        return true
      }

      // AgreementFlowService.userInputtedValuesを参照して条件を満たすかチェック
      return Object.entries(this.enableIf).every(([key, value]) => {
        return this.$afs.userInputtedValues[key] === value
      })
    },
    wrapperClasses(): string[] {
      return [
        this.type === 'checkbox' ? 'd-block' : 'd-flex flex-column',
        this.errorMessage ? this.$afs.inputHasErrorClass : '',
      ]
    },
    /** 署名欄の値が Base64 Data URL か（サーバー変換後の飼い主閲覧用） */
    isBase64DataUrl(): boolean {
      return typeof this.value === 'string' && this.value.startsWith('data:')
    },
    /** 署名画像として扱う文字列か（S3 相対パス or Data URL） */
    isSignatureValue(): boolean {
      return (
        typeof this.value === 'string' &&
        (this.value.startsWith('u/') || this.isBase64DataUrl)
      )
    },
    isDisplayFromClincUser(): boolean {
      // @ts-ignore
      return !!window?.$core?.$embAuth?.user // ?.ps_clinic
    }
  },
  watch: {
    value(newValue) {
      this.$afs.userInputtedValues[this.inputValueKey] = newValue
      if (this.errorMessage) {
        this.$afs.userInputErrors[this.inputValueKey] = this.errorMessage
      } else {
        delete this.$afs.userInputErrors[this.inputValueKey]
      }
    },
    shouldDisplayInput(newValue) {
      if (!newValue) {
        // 非表示になる場合、値とエラーメッセージをクリア
        this.value = null
        delete this.$afs.userInputtedValues[this.inputValueKey]
        delete this.$afs.userInputErrors[this.inputValueKey]
      }
    },
  },
  methods: {
    onInput(event: Event) {
      const inputElement = event.target as HTMLInputElement
      if (this.type === 'checkbox') {
        this.value = inputElement.checked
      } else {
        this.value = inputElement.value
      }
    },
    onMultiselectChange(option: string, event: Event) {
      const inputElement = event.target as HTMLInputElement
      const currentSelected = [...this.selectedValues]

      if (inputElement.checked) {
        // 選択された場合、配列に追加
        if (!currentSelected.includes(option)) {
          currentSelected.push(option)
        }
      } else {
        // 選択解除された場合、配列から削除
        const index = currentSelected.indexOf(option)
        if (index > -1) {
          currentSelected.splice(index, 1)
        }
      }

      // カンマ区切りの文字列として保存
      this.value = currentSelected.join(',')
    },
  },
}
</script>
